import "./CsDashboard.scss";

import React, { useEffect, useMemo, useRef, useState } from "react";

import { Button, message, Space, Table, Tag, Tooltip, Typography } from "antd";
import moment from "moment";

import {
  BarChartOutlined,
  BookOutlined,
  CopyOutlined,
  LinkOutlined,
  MessageOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  TrophyOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";

import fileDownload from "js-file-download";
import axios from "axios";
import CoachProfileModal from "../../Components/CoachProfileModal/CoachProfileModal";
import { useAppTheme } from "../../context/ThemeProvider";
import {
  formatIndianCurrencyToReadableWords,
  formatNumberToReadable,
} from "../../Utils/formatter";
import { copyToClipboard } from "../../Utils/utilityFunctions";
import { getCoaches, getCoachesAnalytics, getCoachesCSV } from "../api";
import CSDashboardFilter from "./CSDashboardFilter";
import DashboardCards from "./DashboardCards";

const { Text, Link } = Typography;

/**
 *
 * Query strings
 {
    "accountManager": [
      "unassigned",
      "60b882ee85918890aa65e561",
      "615d66dcc5236060caae7436"
    ],
    "activeStatus": "inactive",
    "endDate": "2025-09-10T06:29:59.999Z",
    "lastActiveAtEndDate": "2025-09-08T08:38:05.851Z",
    "lastActiveAtStartDate": "2025-09-08T06:38:05.851Z",
    "limit": "10",
    "maxEarnings": "11919023",
    "minEarnings": "983000",
    "page": "1",
    "paymentPlans": [
      "no subscription",
      "monthly"
    ],
    "platformSetups": [
      "enterprise",
      "freedom"
    ],
    "search": "Dhruv type",
    "sortOn": "totalEarnings",
    "sortOrder": "desc",
    "startDate": "2025-08-31T18:30:00.000Z",
    "tierExpiryEndDate": "2025-09-17T10:49:59.252Z",
    "tierExpiryStartDate": "2025-07-17T10:49:59.252Z",
    "tierLastRenewedAtEndDate": "2025-02-30T12:06:10.346Z",
    "tierLastRenewedAtStartDate": "2025-02-19T12:06:10.346Z",
    "tiers": [
      "tier1",
      "tier4"
    ]
  }
 *
 */

// Tier mapping
const TierMapping = {
  tier1: "Basic",
  tier2: "Pro",
  tier3: "Advance",
  tier4: "Ultimate",
};

const CsDashboard = () => {
  const { COLORS } = useAppTheme();
  const staticState = useRef({
    coachAnalyticsDebouncer: null,
    coachListDebouncer: null,
    coachAnalyticsCancelToken: axios.CancelToken.source(),
    coachListCancelToken: axios.CancelToken.source(),
  });

  // State management
  const [coaches, setCoaches] = useState([]);
  const [totalCoaches, setTotalCoaches] = useState(0);
  const [loading, setLoading] = useState({
    coachList: true,
    coachAnalytics: true,
  });
  const [paginationQuery, setPaginationQuery] = useState({
    query: "",
    page: 1,
    limit: 10,
    sortOn: "totalEarnings",
    sortOrder: "desc",
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCoachId, setSelectedCoachId] = useState(null);

  const [analytics, setAnalytics] = useState({
    totalCoaches: 0,
    totalAssignedCoaches: 0,
    totalEarnings: 0,
    totalEarnedCoaches: 0,
    totalActiveCoaches: 0,
    totalActiveCoachesEarnings: 0,
    totalActiveWorkshops: 0,
    totalActiveWorkshopCreators: 0,
    totalActiveOneToOne: 0,
    totalActiveOneToOneCreators: 0,
    totalActiveCourses: 0,
    totalActiveCourseCreators: 0,
    totalCustomers: 0,
    totalFreeCustomers: 0,
    freedomCoaches: {
      tier1: 0,
      tier2: 0,
      tier3: 0,
      tier4: 0,
      tier5: 0,
    },
    enterpriseCoaches: {
      tier1: 0,
      tier2: 0,
      tier3: 0,
      tier4: 0,
      tier5: 0,
    },
  });

  // Handle sorting
  const handleSort = (field) => {
    setPaginationQuery((prev) => {
      const newQuery = { ...prev };

      // If already sorting by this field, toggle the order
      if (prev.sortOn === field) {
        if (prev.sortOrder === "asc") {
          newQuery.sortOrder = "desc";
        } else {
          // Remove sorting if clicking the same field twice
          delete newQuery.sortOn;
          delete newQuery.sortOrder;
        }
      } else {
        // New field, set to ascending
        newQuery.sortOn = field;
        newQuery.sortOrder = "asc";
      }

      return newQuery;
    });
  };

  // Handle pagination
  const handleTableChange = (page, pageSize) => {
    setPaginationQuery((prev) => ({
      ...prev,
      page: prev.limit === pageSize ? page : 1,
      limit: pageSize,
    }));
  };

  // Format plan type (remove -plan suffix)
  const formatPlanType = (planType) => {
    if (!planType) return "N/A";
    return (
      planType.replace("-plan", "").charAt(0).toUpperCase() +
      planType.replace("-plan", "").slice(1)
    );
  };

  // Format date with null handling
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).format("DD/MM/YYYY");
  };

  // Format date and time with null handling
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return {
      date: moment(dateString).format("DD/MM/YYYY"),
      time: moment(dateString).format("hh:mm a"),
    };
  };

  // Format phone number with dial code
  const formatPhone = (phone, dialCode) => {
    if (!phone) return "N/A";
    const phoneStr = phone.toString();
    return `${dialCode || "+91"}-${phoneStr}`;
  };

  // Copy to clipboard handler
  const handleCopy = (text, type) => {
    copyToClipboard(text, `${type} copied successfully`);
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        title: "Name & Contact",
        key: "nameContact",
        width: 300,
        fixed: "left",
        render: (_, record) => (
          <div style={{ padding: "8px 0" }}>
            <div style={{ marginBottom: "4px" }}>
              <Text
                strong
                style={{
                  fontSize: 14,
                  cursor: "pointer",
                  color: "#1890ff",
                  textDecoration: "underline",
                }}
                onClick={() => handleCoachClick(record._id)}
              >
                {record.name || "N/A"}
              </Text>
            </div>
            <div style={{ marginBottom: "2px" }}>
              <Space>
                <Text>{record.email || "N/A"}</Text>
                {record.email && (
                  <Tooltip title="Copy email">
                    <CopyOutlined
                      style={{ cursor: "pointer", color: COLORS.ICON }}
                      onClick={() => handleCopy(record.email, "Email")}
                    />
                  </Tooltip>
                )}
              </Space>
            </div>
            <div style={{ marginBottom: "2px" }}>
              <Space>
                <Text>{formatPhone(record.phone, record.dialCode)}</Text>
                {record.phone && (
                  <Tooltip title="Copy phone">
                    <CopyOutlined
                      style={{ cursor: "pointer", color: COLORS.ICON }}
                      onClick={() => handleCopy(record.phone, "Phone")}
                    />
                  </Tooltip>
                )}
              </Space>
            </div>
            <div>
              <Space>
                <Link
                  href={`https://${record.host}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1890ff" }}
                >
                  {record.host || "N/A"}
                </Link>
                <LinkOutlined style={{ color: COLORS.ICON }} />
              </Space>
            </div>
          </div>
        ),
      },
      {
        title: "Assignment",
        key: "assignment",
        width: 150,
        render: (_, record) => (
          <Text>{record.accountManager?.name || "N/A"}</Text>
        ),
      },
      {
        title: "Plans",
        key: "plans",
        width: 110,
        render: (_, record) => (
          <div>
            <div style={{ marginBottom: 4 }}>
              <Tag
                color={
                  record.whitelabelPlanType?.includes("freedom")
                    ? "blue"
                    : "green"
                }
              >
                {formatPlanType(record.whitelabelPlanType)}
              </Tag>
            </div>
            <div style={{ marginBottom: 4 }}>
              <Tag color="lightgray">
                {TierMapping[record.userTier] || "N/A"}
              </Tag>
            </div>
            <div>
              <Text
                type="secondary"
                style={{ fontSize: 12, textTransform: "capitalize" }}
              >
                {record.paymentPlan || "N/A"}
              </Text>
            </div>
          </div>
        ),
      },
      {
        title: "Performance",
        key: "performance",
        width: 250,
        render: (_, record) => (
          <div
            style={{
              display: "flex",
              flexWrap: "nowrap",
              gap: "1px",
              justifyContent: "space-around",
              alignItems: "flex-start",
              flexDirection: "column",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <UserOutlined style={{ color: COLORS.ICON, fontSize: "10px" }} />
              <Text>
                {formatNumberToReadable(record.totalCustomers || 0)}{" "}
                <Text strong style={{ color: "#16a249" }}>
                  ({formatNumberToReadable(record.totalFreeCustomers || 0)} F)
                </Text>{" "}
                customers
              </Text>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <TrophyOutlined
                style={{ color: COLORS.ICON, fontSize: "10px" }}
              />
              <Text>
                {formatNumberToReadable(record.totalActiveMangos || 0)} services
              </Text>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <VideoCameraOutlined
                style={{ color: COLORS.ICON, fontSize: "10px" }}
              />
              <Text>
                {formatNumberToReadable(record.totalActiveWorkshops || 0)}{" "}
                workshops
              </Text>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <BookOutlined style={{ color: COLORS.ICON, fontSize: "10px" }} />
              <Text>
                {formatNumberToReadable(record.totalActiveCourses || 0)} courses
              </Text>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <MessageOutlined
                style={{ color: COLORS.ICON, fontSize: "10px" }}
              />
              <Text>
                {formatNumberToReadable(record.totalActiveOneToOne || 0)}{" "}
                1-on-1s
              </Text>
            </div>
          </div>
        ),
      },
      {
        title: (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Text>Earnings</Text>
            <Tooltip title="Sort by earnings">
              {(() => {
                if (paginationQuery.sortOn === "totalEarnings") {
                  return paginationQuery.sortOrder === "asc" ? (
                    <SortAscendingOutlined
                      style={{ cursor: "pointer", color: COLORS.ICON }}
                      onClick={() => handleSort("totalEarnings")}
                    />
                  ) : (
                    <SortDescendingOutlined
                      style={{ cursor: "pointer", color: COLORS.ICON }}
                      onClick={() => handleSort("totalEarnings")}
                    />
                  );
                }
                return (
                  <SortDescendingOutlined
                    style={{
                      cursor: "pointer",
                      color: COLORS.ICON,
                      opacity: 0.5,
                    }}
                    onClick={() => handleSort("totalEarnings")}
                  />
                );
              })()}
            </Tooltip>
          </div>
        ),
        dataIndex: "totalEarnings",
        key: "totalEarnings",
        width: 120,
        render: (amount) => (
          <Text strong style={{ fontSize: 16, color: "#16a249" }}>
            {formatIndianCurrencyToReadableWords(amount || 0)}
          </Text>
        ),
      },
      {
        title: (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Text>Last Signed In</Text>
            <Tooltip title="Sort by last signed in">
              {(() => {
                if (paginationQuery.sortOn === "lastActiveAt") {
                  return paginationQuery.sortOrder === "asc" ? (
                    <SortAscendingOutlined
                      style={{ cursor: "pointer", color: COLORS.ICON }}
                      onClick={() => handleSort("lastActiveAt")}
                    />
                  ) : (
                    <SortDescendingOutlined
                      style={{ cursor: "pointer", color: COLORS.ICON }}
                      onClick={() => handleSort("lastActiveAt")}
                    />
                  );
                }
                return (
                  <SortDescendingOutlined
                    style={{
                      cursor: "pointer",
                      color: COLORS.ICON,
                      opacity: 0.5,
                    }}
                    onClick={() => handleSort("lastActiveAt")}
                  />
                );
              })()}
            </Tooltip>
          </div>
        ),
        dataIndex: "lastActiveAt",
        key: "lastActiveAt",
        width: 150,
        render: (dateString) => {
          const dateTime = formatDateTime(dateString);
          if (dateTime === "N/A") {
            return <Text>N/A</Text>;
          }
          return (
            <div>
              <div>{dateTime.date}</div>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                {dateTime.time}
              </div>
            </div>
          );
        },
      },
      {
        title: "Renewals",
        key: "renewals",
        width: 200,
        render: (_, record) => (
          <div>
            <div style={{ marginBottom: 4 }}>
              <Text>Last: {formatDate(record.tierLastRenewedAt)}</Text>
            </div>
            <div>
              <Text>Expiry: {formatDate(record.tierExpiry)}</Text>
            </div>
          </div>
        ),
      },
    ],
    [paginationQuery]
  );

  // Querying the API------------------------------------------------------------------
  const generateQuery = (pQuery = { page: 1, limit: 10, query: "" }) => {
    const query = new URLSearchParams(pQuery.query);
    query.append("page", pQuery.page);
    query.append("limit", pQuery.limit);

    if (pQuery.sortOn) {
      query.append("sortOn", pQuery.sortOn);
    }
    if (pQuery.sortOrder) {
      query.append("sortOrder", pQuery.sortOrder);
    }

    return query.toString();
  };

  const getCoachList = async (fetchQuery) => {
    try {
      setLoading((prev) => ({ ...prev, coachList: true }));

      const query = generateQuery(fetchQuery);
      const resp = await getCoaches(
        query,
        staticState.current.coachListCancelToken.token
      );

      if (resp?.code === 200) {
        setCoaches(resp?.result?.coaches);
        setTotalCoaches(resp?.result?.totalCoaches);
      }
    } catch (error) {
      // if the request is cancelled, then return
      if (axios.isCancel(error)) {
        console.log(">>> coach list request cancelled");
        return;
      }

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "failed to fetch coaches list";

      message.error(errorMessage);
    } finally {
      setLoading((prev) => ({ ...prev, coachList: false }));
    }
  };

  const getCoachOverallAnalytics = async (fetchQuery) => {
    try {
      setLoading((prev) => ({ ...prev, coachAnalytics: true }));

      const query = generateQuery(fetchQuery);
      const resp = await getCoachesAnalytics(
        query,
        staticState.current.coachAnalyticsCancelToken.token
      );

      if (resp?.code === 200 && resp?.result) {
        setAnalytics(resp?.result);
      }
    } catch (error) {
      // if the request is cancelled, then return
      if (axios.isCancel(error)) {
        console.log(">>> coach analytics request cancelled");
        return;
      }

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "failed to fetch coaches analytics";

      message.error(errorMessage);
    } finally {
      setLoading((prev) => ({ ...prev, coachAnalytics: false }));
    }
  };

  const handleExportToCSV = async (
    fetchQuery,
    options = {
      disableErrorHandle: false,
      customFallbackSuccessMessage: "",
      customFallbackErrorMessage: "",
    }
  ) => {
    try {
      const query = generateQuery(fetchQuery);
      const response = await getCoachesCSV(query);

      // Download the file
      const fileName = `coaches-export-${moment().format(
        "YYYY-MM-DD-HH-mm-ss"
      )}.csv`;
      fileDownload(response, fileName);

      message.success(
        options?.customFallbackSuccessMessage ||
          "CSV file downloaded successfully!"
      );
    } catch (error) {
      if (options?.disableErrorHandle) {
        throw error;
      } else {
        message.error(
          error?.message ||
            options?.customFallbackErrorMessage ||
            "failed to export coaches"
        );
      }
    }
  };

  // ----------------------------------------------------------------------------------

  // Generate report handler
  const handleGenerateReport = () => {
    message.info("Generate Report functionality will be implemented");
  };

  // Modal handlers
  const handleCoachClick = (coachId) => {
    setSelectedCoachId(coachId);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedCoachId(null);
  };

  // any changes in the query will trigger the change in the table
  useEffect(() => {
    // Cancel any ongoing request when a new query starts
    staticState.current.coachListCancelToken.cancel("NEW_QUERY_UPDATED");

    if (staticState.current.coachListDebouncer) {
      clearTimeout(staticState.current.coachListDebouncer);
    }

    // Create a new cancel token for the new request
    staticState.current.coachListCancelToken = axios.CancelToken.source();

    // for reducing the number of requests, we are using debouncer.
    staticState.current.coachListDebouncer = setTimeout(() => {
      getCoachList(paginationQuery);
    }, 150);

    return () => {
      clearTimeout(staticState.current.coachListDebouncer);
    };
  }, [paginationQuery]);

  // this is overall analytics, so only the data changes (ie, not sort or pagination) will effect the data
  useEffect(() => {
    // Cancel any ongoing request when a new query starts
    staticState.current.coachAnalyticsCancelToken.cancel("NEW_QUERY_UPDATED");

    if (staticState.current.coachAnalyticsDebouncer) {
      clearTimeout(staticState.current.coachAnalyticsDebouncer);
    }

    // Create a new cancel token for the new request
    staticState.current.coachAnalyticsCancelToken = axios.CancelToken.source();

    // for reducing the number of requests, we are using debouncer.
    staticState.current.coachAnalyticsDebouncer = setTimeout(() => {
      getCoachOverallAnalytics(paginationQuery);
    }, 150);

    return () => {
      clearTimeout(staticState.current.coachAnalyticsDebouncer);
    };
    // only query change will trigger the overall analytics.
    // bcs, all the other pagination query will be on re-arrangement of the data.
  }, [paginationQuery.query]);

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 24,
          }}
        >
          <div>
            <Typography.Title
              level={2}
              style={{ margin: 0, color: "#262626", fontSize: 24 }}
            >
              Coach Management Dashboard
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Manage coach assignments and track performance
            </Typography.Text>
          </div>
          <Space>
            <Button
              type="primary"
              icon={<BarChartOutlined />}
              onClick={handleGenerateReport}
              size="large"
            >
              Generate Report
            </Button>
          </Space>
        </div>

        {/* Dashboard Cards */}
        <DashboardCards
          dashboardMetrics={analytics}
          loading={loading.coachAnalytics}
        />
      </div>

      <CSDashboardFilter
        paginationQuery={paginationQuery}
        setPaginationQuery={setPaginationQuery}
        handleExportToCSV={(options) =>
          handleExportToCSV(paginationQuery, options)
        }
      />

      <Table
        columns={columns}
        dataSource={coaches}
        loading={loading.coachList}
        pagination={{
          current: paginationQuery.page,
          pageSize: paginationQuery.limit,
          total: totalCoaches,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} users`,
          onChange: handleTableChange,
          // onShowSizeChange: handleTableChange,
        }}
        scroll={{ x: 1500 }}
        style={{
          backgroundColor: COLORS.SURFACE,
          borderColor: COLORS.BORDER,
        }}
        rowKey="_id"
        locale={{
          emptyText: loading.coachList ? "Loading..." : "No coaches found",
        }}
      />

      {/* Coach Profile Modal */}
      <CoachProfileModal
        visible={modalVisible}
        onClose={handleModalClose}
        coachId={selectedCoachId}
      />

      {/* CSV Export Confirmation Modal */}
    </>
  );
};

export default CsDashboard;
